import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import Button from "../../components/Button";
import SectionLabel from "../../components/SectionLabel";
import { EditProfileModal } from "../../components/EditProfileModal/EditProfileModal";
import ButtonPrimary from "../../components/ButtonPrimary";
import Card from "../../components/Card";
import Grid, { Col, Row } from "../../components/Grid";
import Icon from "../../components/Icon";
import { IconName } from "../../components/Icon/types";
import ProfileCard from "../../components/ProfileCard";
import { IUserUpdatePayload } from "../../services/api/user/types";
import userAPIService from "../../services/api/user";
import authPasswordAPIService from "../../services/api/auth";
import Toaster from "../../services/toaster";
import { ACTION, SUBJECT } from "../../constants/permissions";
import { useAbility } from "../../hooks";
import { getExistErrorKey } from "../../utils/common/errors";
import {
  fetchCurrentPrincipal,
  selectCustomerData,
  selectPrincipalData,
} from "../../store/user";
import { selectTenants } from "../../store/metadata";
import { IErrorDetail } from "../../utils/common/types";
import { RESPONSE_ERROR_PROBLEM } from "../../utils/common/contants";
import { ConfirmModal } from "../../components/ConfirmModal/ConfirmModal";

import "./styles.scss";

export const PrincipalDBView: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userData = useSelector(selectPrincipalData);
  const customerData = useSelector(selectCustomerData);
  const originData = useSelector(selectTenants).find((tenant) => {
    return tenant.tenant_code === userData?.tenant_code;
  });
  const isCorporate = customerData?.is_corporate;
  const ability = useAbility();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [isFetchingEditProfile, setIsFetchingEditProfile] = useState(false);
  const [isFetchingResetPassword, setIsFetchingResetPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { id: principalId } = useParams();
  const canUpdatePrincipal =
    ability.can(ACTION.WRITE, SUBJECT.USER) ||
    ability.can(ACTION.WRITE, SUBJECT.MANAGE);
  const isEmailAvailable = useMemo(() => {
    return (
      originData?.settings?.user_context
        ?.principal_email_synchronized_from_username === false
    );
  }, [originData]);

  const isUsernameDisabled = useMemo(() => {
    return (
      originData?.settings?.user_context?.principal_username_is_immutable ===
      true
    );
  }, [originData]);

  const openProfileModal = () => {
    setShowEditDialog(true);
  };

  const closeProfileModal = () => {
    setShowEditDialog(false);
  };

  const openResetPasswordModal = () => {
    setShowResetPasswordDialog(true);
  };

  const closeResetPasswordModal = () => {
    setShowResetPasswordDialog(false);
  };

  const handleResetPassword = async () => {
    if (!userData) {
      return;
    }

    try {
      setIsFetchingResetPassword(true);
      await authPasswordAPIService.resetPassword({
        username: userData.username,
        // eslint-disable-next-line @typescript-eslint/camelcase
        tenant_code: userData.tenant_code,
      });
      Toaster.success({ content: t("resetPasswordEmailSent") });
    } catch (error) {
      Toaster.error({ content: t("resetPasswordEmailFailed") });
    } finally {
      setIsFetchingResetPassword(false);
      closeResetPasswordModal();
    }
  };

  const handleEditProfileSubmit = async (
    data: IUserUpdatePayload,
    businessName: string,
  ) => {
    try {
      setIsFetchingEditProfile(true);
      await userAPIService.update(data, principalId);

      if (isCorporate) {
        await userAPIService.editCustomer(customerData?.id, {
          name: businessName,
        });
      }

      closeProfileModal();
      Toaster.success({ content: t("profileUpdatedSuccessfully") });
      dispatch(fetchCurrentPrincipal(principalId));
    } catch (error) {
      const errorDetails = error.response?.data?.details;
      const receivedErrors: Record<string, string> = {};
      let isHandledError = false;

      errorDetails?.forEach((errorDetail: IErrorDetail) => {
        if (error.response?.status === 409) {
          Toaster.error({
            content: t(getExistErrorKey(errorDetail)),
          });
          isHandledError = true;
          return;
        }

        if (error?.response?.status === 422) {
          if (errorDetail.path === "current_password") {
            receivedErrors.password = "InvalidCurrentPassword";
            isHandledError = true;
          }

          if (
            errorDetail.path === "email" &&
            errorDetail.problem === RESPONSE_ERROR_PROBLEM.Illegal
          ) {
            receivedErrors.email = "latinCharactersOnly";
            isHandledError = true;
          }

          if (
            errorDetail.path === "email" &&
            (errorDetail.problem === RESPONSE_ERROR_PROBLEM.InvalidValue ||
              errorDetail.problem === RESPONSE_ERROR_PROBLEM.OutOfRange)
          ) {
            receivedErrors.email = "InvalidEmailFormat";
            isHandledError = true;
          }

          if (
            errorDetail.path === "name" &&
            errorDetail.problem === RESPONSE_ERROR_PROBLEM.InvalidValue
          ) {
            receivedErrors.name = "NameIsTooLong";
            isHandledError = true;
          }
        }
      });

      setErrors(receivedErrors);

      if (isHandledError) {
        return;
      }
      Toaster.error({
        content: error.message || t("somethingWentWrong"),
      });
    } finally {
      setIsFetchingEditProfile(false);
    }
  };

  if (!userData) {
    return null;
  }

  return (
    <section className="ams-profile">
      <header>
        <h1 className="page-title">{t("profile")}</h1>
      </header>
      <ProfileCard
        isShowEmail={isEmailAvailable}
        actionText={t("updateProfileInfo")}
        titleText={t("profileInfo")}
        handleAction={openProfileModal}
        canHandleAction={canUpdatePrincipal}
        name={userData?.name}
        businessName={isCorporate ? customerData?.name : ""}
        brandCode={userData?.tenant_code}
        email={userData?.email}
        username={userData?.username}
      />

      <Card>
        <h2>{t("password")}</h2>
        <Grid>
          <Row>
            <Col>
              <SectionLabel>{t("password")}</SectionLabel>
            </Col>
            <Col className="ams-col__value">
              <SectionLabel>••••••••••••</SectionLabel>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button disabled>{t("changePassword")}</Button>
            </Col>
            <Col className="ams-col__value">
              {canUpdatePrincipal && (
                <ButtonPrimary
                  className="ams-button--primary-icon"
                  onClick={openResetPasswordModal}
                >
                  <Icon>{"admin-inverse" as IconName}</Icon>
                  {t("sendPasswordResetEmail")}
                </ButtonPrimary>
              )}
            </Col>
          </Row>
        </Grid>
      </Card>
      {showEditDialog && canUpdatePrincipal && (
        <EditProfileModal
          isPasswordDisabled
          isUserNameDisabled={isUsernameDisabled}
          isSyncEmail={!isEmailAvailable}
          isOpen={showEditDialog}
          user={userData}
          businessName={isCorporate ? customerData?.name : ""}
          errors={errors}
          isLoading={isFetchingEditProfile}
          onDismiss={closeProfileModal}
          onSubmit={handleEditProfileSubmit}
        />
      )}
      {showResetPasswordDialog && canUpdatePrincipal && (
        <ConfirmModal
          title={t("resetPassword")}
          message={t("resetPasswordConfirmation", { email: userData.email })}
          submitBtnText={t("sendPasswordResetEmail")}
          isLoading={isFetchingResetPassword}
          isOpen={showResetPasswordDialog}
          onDismiss={closeResetPasswordModal}
          onSubmit={handleResetPassword}
          className={"ams-reset-password-modal"}
        />
      )}
    </section>
  );
};
